import * as React from "react"
import { activityAttribution, trackActivity } from "../activity"
import { jsonFetch } from "../bookingApi"
import { calendarPostcode } from "../dateSelection"
import { useCheckoutDetailsAutosave } from "./useCheckoutDetailsAutosave"
import { resultError } from "../stripe"
import type { DetailFieldName, HireBookingProps } from "../types"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "phase" | "journeyStep" | "draftRestored" | "manualDeliveryAddress" | "manualDeliveryEntry" | "selectedDeliveryAddress" | "customer" | "postcode" | "quote" | "setDetailErrors" | "setValidationAttempt" | "detailsPanelRef" | "actionsRef" | "confirmationToken" | "differentBilling" | "billingAddress" | "billingComplete">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "baseUrl">
    & { props: HireBookingProps }

export function useCustomerDetails(inputs: Inputs) {
    const {
        manualDeliveryAddress,
        manualDeliveryEntry,
        selectedDeliveryAddress,
        customer,
        postcode,
        quote,
        setDetailErrors,
        setValidationAttempt,
        detailsPanelRef,
        actionsRef,
        baseUrl,
        confirmationToken,
        props,
        differentBilling,
        billingAddress,
        billingComplete,
    } = inputs
    const completeManualDeliveryAddress = manualDeliveryAddress.line1.trim().length >= 3 && manualDeliveryAddress.city.trim().length >= 2
    const hasDeliveryAddress = manualDeliveryEntry ? completeManualDeliveryAddress : Boolean(selectedDeliveryAddress)

    const validEmail = customer.email.trim().length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email.trim())
    const validPhone = /^[+0-9()\s-]{7,30}$/.test(customer.phone.trim())
    const detailsComplete = validEmail && validPhone && hasDeliveryAddress

    React.useEffect(() => {
        if (customer.name || customer.email || customer.phone) trackActivity("details_started", {}, true)
        if (detailsComplete) trackActivity("details_completed", {}, true)
    }, [customer.name, customer.email, customer.phone, detailsComplete])
    const lastTrackedPostcode = React.useRef("")
    React.useEffect(() => {
        const normalized = calendarPostcode(postcode)
        if (!normalized || normalized === lastTrackedPostcode.current) return
        lastTrackedPostcode.current = normalized
        trackActivity("postcode_entered", { postcode: normalized })
    }, [postcode])
    const currentDeliveryAddress = () => {
        const deliveryPostcode = quote?.postcode || postcode.trim().toUpperCase()
        if (!hasDeliveryAddress) return null
        const address = manualDeliveryEntry
            ? {
                line1: manualDeliveryAddress.line1.trim(),
                line2: manualDeliveryAddress.line2.trim() || null,
                city: manualDeliveryAddress.city.trim(),
                postal_code: deliveryPostcode,
                country: "GB",
            }
            : selectedDeliveryAddress
        return address
    }

    const draftAddress = manualDeliveryEntry
        ? (manualDeliveryAddress.line1.trim() || manualDeliveryAddress.line2.trim() || manualDeliveryAddress.city.trim() ? {
            line1: manualDeliveryAddress.line1.trim(), line2: manualDeliveryAddress.line2.trim() || null,
            city: manualDeliveryAddress.city.trim(), postal_code: quote?.postcode || postcode.trim().toUpperCase(), country: "GB",
        } : undefined)
        : selectedDeliveryAddress || undefined
    const draftValues = {
        email: validEmail ? customer.email.trim().toLowerCase() : undefined,
        phone: validPhone ? customer.phone.trim() : undefined,
        deliveryAddress: draftAddress && draftAddress.line1.length <= 200 && (draftAddress.line2?.length || 0) <= 200 && draftAddress.city.length <= 100 ? draftAddress : undefined,
    }
    const autosave = useCheckoutDetailsAutosave({
        baseUrl, confirmationToken, detailsPanelRef, values: draftValues,
        enabled: inputs.draftRestored && inputs.journeyStep === "payment" && (inputs.phase === "ready" || inputs.phase === "initialising"),
    })

    const clearDetailError = (field: DetailFieldName) => {
        setDetailErrors((current) => {
            if (!current[field]) return current
            const next = { ...current }
            delete next[field]
            return next
        })
    }

    const validateDetails = () => {
        const next: Partial<Record<DetailFieldName, string>> = {}
        if (!validEmail) next.email = "Enter a valid email address."
        if (!validPhone) next.phone = "Enter a valid phone number."
        if (!hasDeliveryAddress) next.address = manualDeliveryEntry ? "Enter a complete delivery address." : "Select your delivery address."
        setDetailErrors(next)
        const first = (Object.keys(next) as DetailFieldName[])[0]
        if (!first) return true
        trackActivity("validation_failed", { field: first })
        setValidationAttempt((current) => current + 1)
        window.setTimeout(() => {
            const panel = detailsPanelRef.current
            const field = `[data-detail-field="${first}"]`
            const invalid = panel?.querySelector<HTMLElement>(`${field}[aria-invalid="true"]`) || panel?.querySelector<HTMLElement>(field)
            invalid?.focus()
        }, 0)
        return false
    }

    const saveCustomerDetails = async (termsAccepted: boolean, wallet = false) => {
        // The final submission never waits for background traffic.
        const submissionAutosave = autosave.pause()
        const deliveryAddress = currentDeliveryAddress()
        if (!deliveryAddress) throw new Error("Enter a complete delivery address.")
        if (!validEmail) throw new Error("Enter a valid email address.")
        if (!validPhone) throw new Error("Enter a valid mobile phone number.")
        if (!wallet && differentBilling && (!billingComplete || !billingAddress)) throw new Error("Enter a complete billing address.")
        {
            const actions = actionsRef.current
            if (!actions) throw new Error("Checkout is still loading. Try again in a moment.")
            for (const result of [
                await actions.updateEmail(customer.email.trim()),
                await actions.updatePhoneNumber(customer.phone.trim()),
                // The Billing Address Element owns a separate address; wallets
                // supply their own. Only send delivery here when it is shared.
                ...(wallet || differentBilling ? [] : [await actions.updateBillingAddress({ address: deliveryAddress })]),
            ]) {
                const message = resultError(result)
                if (message) throw new Error(message)
            }
        }
        await jsonFetch(`${baseUrl}/v1/booking-details`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                confirmationToken,
                nameCollection: "payment",
                email: customer.email,
                phone: customer.phone,
                deliveryAddress: deliveryAddress,
                billingAddress: !wallet && differentBilling ? billingAddress?.address : null,
                companyName: customer.companyName,
                deliveryInstructions: customer.deliveryInstructions,
                attribution: activityAttribution(props.analyticsVariant),
                termsAccepted,
                termsVersion: termsAccepted ? quote?.terms.version : undefined,
            }),
        })
        submissionAutosave?.acknowledgeFinal(draftValues)
    }

    return {
        detailsComplete,
        currentDeliveryAddress,
        clearDetailError,
        saveCustomerDetails,
        validateDetails,
    }
}
